import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import {
	Box, Button, Card, CardHeader, CardContent, CardActions, Grid, Divider, colors,
	TextField, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Typography, CircularProgress
} from '@mui/material';
import clsx from 'clsx';
import validate from 'validate.js';
import { FilePicker } from 'components';

import { enqueueSnackbar } from 'actions';
import {
	SetFormState, GenerateTrxReductionFromText, GenerateTrxReductionFromFile
} from '../../../actions/TrxReductionActions.js';
import { INPUT_MODE_TEXT, INPUT_MODE_FILE } from '../../../reducers/TrxReductionReducer.js';

const useStyles = makeStyles((theme) => ({
	content: {
		marginTop: theme.spacing(3),
		border: `2px solid ${colors.blue[600]}`,
	},
	badge: {
		display: 'inline-block',
		padding: '2px 8px',
		borderRadius: 4,
		color: theme.palette.white,
		fontSize: '0.75rem',
		fontWeight: 600,
		letterSpacing: 0.5,
		marginRight: theme.spacing(1),
	},
	badgeColor: {
		backgroundColor: colors.green[600],
	},
	cardHeaderTitle: {
		marginBottom: theme.spacing(1),
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.blue[600],
		'&:hover': {
			backgroundColor: colors.blue[900],
		},
	},
}));

const MAX_FILE_SIZE = 100000000;

// The rules change with the chosen input, so there is one schema for each.
const textSchema = {
	siteIdsText: {
		presence: { allowEmpty: false, message: 'is required' },
	},
};

const fileSchema = {
	siteIdsFile: {
		presence: { allowEmpty: false, message: 'is required' },
	},
};

const TrxReductionForm = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.trxReductionReducer.formState.trxReduction);

	const [isGenerating, setIsGenerating] = useState(false);

	const inputMode = formState.values.inputMode;
	const isFileMode = inputMode === INPUT_MODE_FILE;

	// The validation is not kept in the state: it is only a result of the values,
	// so it is calculated on every render. This also avoids an extra render per keystroke.
	const errors = validate(formState.values, isFileMode ? fileSchema : textSchema);
	const isValid = !errors;

	// Keeps the rest of the form state and only replaces the given values.
	const _setValues = (values, touchedField) => {
		setFormState({
			trxReduction: {
				...formState,
				values: {
					...formState.values,
					...values,
				},
				touched: {
					...formState.touched,
					[touchedField]: true,
				},
			}
		});
	};

	const _onChangeMode = (event) => {
		_setValues({ inputMode: event.target.value }, 'inputMode');
	};

	const _onChangeText = (event) => {
		_setValues({ siteIdsText: event.target.value }, 'siteIdsText');
	};

	const _onChangePlanName = (event) => {
		_setValues({ planName: event.target.value }, 'planName');
	};

	const _onChangeFile = (event) => {
		if (event.target.files && event.target.files.length > 0) {
			_setValues({ siteIdsFile: event.target.files[0] }, 'siteIdsFile');
		}
	};

	// Counts the site ids the same way the API does: separated by comma, semicolon,
	// space or new line, repeated ones counted only once.
	const _countSiteIds = (text) => {
		if (!text) {
			return 0;
		}

		const parts = text.split(/[\s,;]+/).filter(value => value.length > 0);
		const uniqueParts = new Set(parts.map(value => value.toUpperCase()));

		return uniqueParts.size;
	};

	const _generate = async () => {
		const planName = formState.values.planName;

		setIsGenerating(true);

		if (isFileMode) {
			const file = formState.values.siteIdsFile;

			if (file.size > MAX_FILE_SIZE) {
				dispatch(enqueueSnackbar({ message: 'File size cannot be superior to 100MB', options: { variant: 'warning' } }));
				setIsGenerating(false);
				return;
			}

			await dispatch(GenerateTrxReductionFromFile(file, planName));
		} else {
			await dispatch(GenerateTrxReductionFromText(formState.values.siteIdsText, planName));
		}

		setIsGenerating(false);
	};

	const siteIdCount = _countSiteIds(formState.values.siteIdsText);

	return (
		<Card className={classes.content}>
			<CardHeader
				classes={{ title: classes.cardHeaderTitle }}
				title={
					<Box display="flex" alignItems="center">
						<span className={clsx(classes.badge, classes.badgeColor)}>GENERATE SCRIPTS</span>
						<Typography variant="body2"><strong></strong></Typography>
					</Box>
				}
				subheader="Give the site ids in a txt file (one per line) or type them separated by commas"
			/>
			<Divider />
			<CardContent>
				<Grid container spacing={3}>
					<Grid item md={12} xs={12}>
						<FormControl>
							<FormLabel>Input</FormLabel>
							<RadioGroup row value={inputMode} onChange={_onChangeMode}>
								<FormControlLabel value={INPUT_MODE_TEXT} control={<Radio />} label="Type the site ids" />
								<FormControlLabel value={INPUT_MODE_FILE} control={<Radio />} label="Upload a txt file" />
							</RadioGroup>
						</FormControl>
					</Grid>

					{isFileMode ? (
						<Grid item md={4} xs={12}>
							<FilePicker value={formState.values.siteIdsFile?.name} onChange={_onChangeFile} accept=".txt" />
						</Grid>
					) : (
						<Grid item md={8} xs={12}>
							<TextField
								fullWidth
								multiline
								minRows={4}
								label="Site ids"
								placeholder="22909, 98676, 10852"
								variant="outlined"
								value={formState.values.siteIdsText}
								onChange={_onChangeText}
								helperText={siteIdCount === 1 ? '1 site id' : `${siteIdCount} site ids`}
							/>
						</Grid>
					)}

					<Grid item md={4} xs={12}>
						<TextField
							fullWidth
							label="Plan name (optional)"
							placeholder="celfi"
							variant="outlined"
							value={formState.values.planName}
							onChange={_onChangePlanName}
							helperText="Used in the plan files. Empty means the current date and time."
						/>
					</Grid>
				</Grid>
			</CardContent>
			<Divider />
			<CardActions>
				<Button
					className={classes.generate}
					onClick={_generate}
					variant="contained"
					disabled={!isValid || isGenerating}
					startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : null}
				>
					{isGenerating ? 'Generating...' : 'Generate'}
				</Button>
			</CardActions>
		</Card>
	);
};

export default TrxReductionForm;
